// Which machines can make a given thing, and which one should.
//
// Pure: capabilities are passed in, so this works the same against the seed file
// and against whatever the machine-admin screen eventually stores.
//
// Two rules run through everything here:
//
//   "Can" and "should" are different questions. SKIP2250 can strand 4 mm² and
//   DTB1250 can strand 25 mm², but both are last resorts. A router with one
//   notion of capability either forbids real work or routes it somewhere daft.
//
//   A limit nobody has told us is not a limit of infinity. Six machines have no
//   recorded maximum cable diameter; those are reported as unchecked rather than
//   silently passed, because a plan that looks verified and is not is worse than
//   one that admits what it does not know.

import { LEVEL, LEVEL_RANK } from './machineCapabilities.js';

// Number(null) is 0, so the obvious one-liner turns "limit not recorded" into
// "limit of zero" and rejects everything. Absence has to stay absent.
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const up = (v) => String(v ?? "").trim().toUpperCase();

/** The rules for one phase on one machine, with machine-level defaults filled in. */
function phaseRules(machine, phase) {
  const wanted = String(phase || "").trim();
  return (machine.phases || [])
    .filter((p) => p.phase === wanted)
    .map((p) => ({
      ...p,
      sectionMin: num(p.sectionMin) ?? num(machine.limits?.sectionMin),
      sectionMax: num(p.sectionMax) ?? num(machine.limits?.sectionMax),
    }));
}

/**
 * Does this product key name a standard the machine is allowed to run?
 *
 * Matching is on the leading name segment, not a prefix of the whole key, so
 * CYABYF does not count as CYABY — those are different cables.
 */
const productName = (productKey) => up(String(productKey).split("|")[0]);

function productAllowed(machine, productKey) {
  if (!productKey) return true; // nothing to check against
  const name = productName(productKey);

  if (machine.productsExcluded?.some((p) => name === up(p))) return false;
  if (!machine.products?.length) return true; // otherwise unrestricted
  return machine.products.some((p) => name === up(p));
}

/**
 * Can this machine do this work, and how happily?
 *
 * @returns {{ ok, level, reasons: [], unchecked: [], note }}
 */
export function evaluateMachine(machine, req = {}) {
  const reasons = [];
  const unchecked = [];

  if (machine.active === false) reasons.push("machine is out of service");

  const rules = phaseRules(machine, req.phase);
  if (!rules.length) {
    return { ok: false, level: null, reasons: [`cannot do ${req.phase}`], unchecked, note: null };
  }

  const section = num(req.section);
  const conductors = num(req.conductors);
  const outer = num(req.outerDiameter);

  // Of the rules for this phase, keep those whose section window fits, then take
  // the best level available — a machine listed both as primary in one range and
  // fallback in another should answer with the one that actually applies.
  const fitting = rules.filter((r) => {
    if (section == null) return true;
    if (r.sectionMin != null && section < r.sectionMin) return false;
    if (r.sectionMax != null && section > r.sectionMax) return false;
    return true;
  });

  if (!fitting.length) {
    const windows = rules.map((r) => `${r.sectionMin ?? "?"}-${r.sectionMax ?? "?"}`).join(", ");
    reasons.push(`section ${section} mm² outside ${windows} mm²`);
  }
  if (section == null) unchecked.push("section not given");

  const best = fitting.sort((a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level])[0] || null;

  const cMin = num(machine.limits?.conductorsMin);
  const cMax = num(machine.limits?.conductorsMax);
  if (conductors != null && cMin != null && cMax != null && (conductors < cMin || conductors > cMax)) {
    reasons.push(`${conductors} conductors outside ${cMin}-${cMax}`);
  }

  const outerMax = num(machine.limits?.outerDiameterMax);
  if (outer != null && outerMax != null && outer > outerMax) {
    reasons.push(`cable Ø ${outer} mm over the ${outerMax} mm limit`);
  } else if (outer != null && outerMax == null) {
    unchecked.push("maximum cable diameter not recorded for this machine");
  }

  if (!productAllowed(machine, req.productKey)) {
    const name = productName(req.productKey);
    reasons.push(
      machine.productsExcluded?.some((p) => name === up(p))
        ? `does not run ${name}`
        : `only runs ${machine.products.join(", ")}`
    );
  }

  // Drum fit, when the caller knows which drum is going on.
  const takeUp = machine.takeUp;
  const drumD = num(req.drumDiameter);
  const drumKg = num(req.drumWeightKg);
  if (drumD != null || drumKg != null) {
    if (!takeUp) {
      // Coiling machines have no take-up by design; a drum request makes no sense.
      reasons.push("no take-up — this machine winds coils, not drums");
    } else {
      if (drumD != null && num(takeUp.maxDrumDiameter) != null && drumD > takeUp.maxDrumDiameter) {
        reasons.push(`drum Ø ${drumD} mm over the take-up limit of ${takeUp.maxDrumDiameter} mm`);
      }
      if (drumKg != null && num(takeUp.maxWeightKg) != null && drumKg > takeUp.maxWeightKg) {
        reasons.push(`${drumKg} kg over the take-up limit of ${takeUp.maxWeightKg} kg`);
      }
    }
  }

  return {
    ok: reasons.length === 0 && !!best,
    level: best?.level ?? null,
    reasons,
    unchecked,
    note: best?.note ?? null,
  };
}

/**
 * Every machine that can do this work, best first.
 *
 * `preferred` is the machine to actually use. It is null when the only options
 * are fallbacks — the caller should decide whether an extreme-case route is
 * acceptable rather than have one chosen quietly.
 */
export function machinesFor(req = {}, capabilities = []) {
  const eligible = [];
  const rejected = [];

  for (const machine of capabilities) {
    const verdict = evaluateMachine(machine, req);
    const row = {
      machineId: machine.machineId,
      name: machine.name,
      level: verdict.level,
      note: verdict.note,
      unchecked: verdict.unchecked,
    };
    if (verdict.ok) eligible.push(row);
    else rejected.push({ ...row, reasons: verdict.reasons });
  }

  eligible.sort(
    (a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level] || a.machineId.localeCompare(b.machineId)
  );

  const routine = eligible.filter((m) => m.level !== LEVEL.FALLBACK);
  const unchecked = [...new Set(eligible.flatMap((m) => m.unchecked))];

  return {
    eligible,
    rejected,
    preferred: routine[0] || null,
    fallbackOnly: eligible.length > 0 && routine.length === 0,
    soleSource: routine.length === 1,
    unchecked,
  };
}

/**
 * Phases only one machine can perform.
 *
 * Worth knowing before scheduling rather than after: a phase with one machine is
 * a hard serialisation point, and that machine's queue IS the delivery date for
 * everything needing it.
 */
export function soleSourcePhases(capabilities = []) {
  const byPhase = new Map();
  for (const machine of capabilities) {
    for (const p of machine.phases || []) {
      if (p.level === LEVEL.FALLBACK) continue; // a last resort is not cover
      if (!byPhase.has(p.phase)) byPhase.set(p.phase, new Set());
      byPhase.get(p.phase).add(machine.machineId);
    }
  }
  return [...byPhase.entries()]
    .filter(([, ids]) => ids.size === 1)
    .map(([phase, ids]) => ({ phase, machineId: [...ids][0] }));
}

/** Everything the capability data does not yet know, so it can be filled in. */
export function capabilityGaps(capabilities = []) {
  const gaps = [];
  for (const m of capabilities) {
    if (m.limits?.outerDiameterMax == null) {
      gaps.push({ machineId: m.machineId, field: "limits.outerDiameterMax" });
    }
    for (const a of m.assumptions || []) {
      gaps.push({ machineId: m.machineId, assumption: a });
    }
  }
  return gaps;
}
